/**
 * Lot interaction for the SVG composed overlay.
 *
 * Manages hover tracking, click handling, and drag detection on the SVG root via
 * event delegation. All state is internal; the caller receives { bind, unbind }
 * to attach and detach listeners from the SVG element.
 *
 * @module mapLibrary/svgLotInteraction
 */

import { isDragGesture } from './overlayUtils';

/**
 * Walks up from a DOM node to find the nearest ancestor-or-self that has an id,
 * stopping before (and not returning) the SVG root element.
 *
 * Lot polygons in the composed SVG are typically <g id="..."> containers whose
 * child <path> elements have no id. event.target gives the leaf <path>;
 * this function climbs to the lot group.
 *
 * When `boundarySelector` is given, an id-bearing node is only accepted if it (or an
 * ancestor of it) matches that selector — this rejects id-bearing elements from unrelated
 * SVG layers (Buildings, Amenity, LotGroup, etc. can carry ids too) that would otherwise be
 * mistaken for a lot.
 *
 * @param {Element} target
 * @param {Element} root - Boundary element (svgElement); never returned
 * @param {string} [boundarySelector] - CSS selector the matched element must be inside
 * @returns {Element|null}
 */
function findLotElement(target, root, boundarySelector) {
  let node = target;
  while (node && node !== root) {
    if (node.id && (!boundarySelector || node.closest(boundarySelector))) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Wires lot hover and click interactions onto an SVG element.
 *
 * @param {SVGElement} svgElement
 * @param {object} handlers
 * @param {Function} [handlers.onLotClick]  - Called with { lotId } on click
 * @param {Function} [handlers.onLotHover]  - Called with { lotId } when the active lot changes
 * @param {Function} [handlers.onLotEnter]  - Called with { target, svgElement } on every target change
 * @param {Function} [handlers.onLotLeave]  - Called with { svgElement } when hover leaves the
 *   current lot — either the pointer moved to non-lot space within the SVG, or left the SVG
 *   entirely
 * @param {Function} [handlers.onBackgroundClick] - Called (no args) on a non-drag click that
 *   misses every lot — findLotElement returned null
 * @param {string} [handlers.boundarySelector] - CSS selector; only id-bearing elements inside
 *   this selector are treated as lots (see findLotElement)
 * @returns {{ bind: Function, unbind: Function }}
 */
function createLotInteraction(svgElement, {
  onLotClick, onLotHover, onLotEnter, onLotLeave, onBackgroundClick, boundarySelector,
}) {
  const state = {
    currentLotId:   null,
    pointerDownPos: null,
    currentTarget:  null,
    rafId:          null,
  };

  function handleMouseDown(event) {
    state.pointerDownPos = { x: event.clientX, y: event.clientY };
  }

  function handleClick(event) {
    if (isDragGesture(state.pointerDownPos, event.clientX, event.clientY)) {
      return;
    }
    const lotElement = findLotElement(event.target, svgElement, boundarySelector);
    if (lotElement) {
      onLotClick?.({ lotId: lotElement.id });
    } else {
      onBackgroundClick?.();
    }
  }

  function handleMouseMove(event) {
    if (state.rafId) {
      return;
    }
    state.rafId = requestAnimationFrame(() => {
      state.rafId = null;
      const lotElement = findLotElement(event.target, svgElement, boundarySelector);
      const newLotId   = lotElement ? lotElement.id : null;

      if (event.target !== state.currentTarget) {
        state.currentTarget = event.target;
        onLotEnter?.({ target: event.target, svgElement });
      }

      if (newLotId === state.currentLotId) {
        return;
      }
      if (newLotId) {
        onLotHover?.({ lotId: newLotId });
      } else {
        onLotLeave?.({ svgElement });
      }
      state.currentLotId = newLotId;
    });
  }

  function handleMouseLeave() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
    state.currentTarget = null;
    onLotLeave?.({ svgElement });
    state.currentLotId = null;
  }

  function bind() {
    svgElement.addEventListener('mousedown',  handleMouseDown);
    svgElement.addEventListener('click',      handleClick);
    svgElement.addEventListener('mouseleave', handleMouseLeave);
    svgElement.addEventListener('mousemove',  handleMouseMove);
  }

  function unbind() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
    svgElement.removeEventListener('mousedown',  handleMouseDown);
    svgElement.removeEventListener('click',      handleClick);
    svgElement.removeEventListener('mouseleave', handleMouseLeave);
    svgElement.removeEventListener('mousemove',  handleMouseMove);
  }

  return { bind, unbind };
}

export default createLotInteraction;
